import { BasicAuthServiceHelper } from '@/modules/Auth'
import { InsurancesServiceHelper } from '@/modules/Insurances'
import { AppDispatch } from '@/store'
import { ApiNotification, Insurance, PricePlan, ShadowOptin } from '@/data'
import { UserTypes } from './UserReducer'
import { convertToAPIReadyBase64Async, RecursivePartial } from '@/utils'
import { User } from '@/dataV3/User/User'
import { ConnectedUser } from '@/dataV3/User/ConnectedUser'
import {
  Animals,
  ContinueRegister,
  CurrentAccount,
  DeleteAccount,
  DisableAccount,
  GetApiNotifications,
  GetShadowOptins,
  GooglePlaceAutocomplete,
  GooglePlaceRetrieve,
  IDCheck,
  MapBoxAutocomplete,
  PartnerOptins,
  Pictures,
  Prices,
  RenewSubscription,
  SetInsurance,
  UpdatePassword,
} from './apiTypes'
import { Picture } from '@/dataV3/Common/Picture'
import { IRI } from 'emt-library'
import { requestAuthenticatedV2Async } from '@/modules/Auth/BasicAuthServiceHelper'
import { Animal } from '@/dataV3/Animal/Animal'
import { ApiNotificationsTypes } from '@/modules/ApiNotifications'
import { _saveAuthInfo } from '@/modules/Auth/LocalStorageHelper'
import { SearchAddressParams, SearchWithGeo, SearchWithoutGeo } from './apiTypes/GooglePlaceAutocomplete'
import { Address, AddressProvider } from '@/dataV3/Common/Address'

/* *******************************
  SETTINGS
******************************* */
export const dispatchGetShadowOptinsAsync = (dispatch: AppDispatch) => async (): Promise<ShadowOptin[]> => {
  let shadowOptins: ShadowOptin[]
  try {
    ;({ items: shadowOptins } =
      await BasicAuthServiceHelper.getAllHydraCollectionPagesV2Async<GetShadowOptins.Response>(GetShadowOptins.Route))
  } catch (error) {
    console.warn('Cannot GET ShadowOptins', error.message)
    shadowOptins = []
  }

  dispatch({ type: UserTypes.UpdateShadowOptins, payload: shadowOptins })

  return shadowOptins
}

export const getPartnerOptinsAsync = async (): Promise<PartnerOptins.GetResponse> => {
  return await requestAuthenticatedV2Async<PartnerOptins.GetResponse>(PartnerOptins.GetRoute)
}

export const createPartnerOptinAsync = async (body: PartnerOptins.PostBody): Promise<PartnerOptins.PostResponse> => {
  return await requestAuthenticatedV2Async<PartnerOptins.PostResponse, PartnerOptins.PostBody>(
    PartnerOptins.PostRoute,
    {
      body,
    },
  )
}

export const deletePartnerOptinAsync = async (optinSlug: string): Promise<PartnerOptins.DeleteResponse> => {
  return await requestAuthenticatedV2Async<PartnerOptins.DeleteResponse>(PartnerOptins.DeleteRoute(optinSlug))
}

/* *******************************
  MY INFORMATION
******************************* */

export const dispatchGetMyInfoAsync =
  (dispatch?: AppDispatch) =>
  /**
   * Will refresh the current user and set it in Reducer
   * @returns The new Customer
   */
  async (paramsToDispatch?: { reactivation?: boolean }) => {
    const user = await BasicAuthServiceHelper.requestAuthenticatedV2Async<CurrentAccount.Get.Response>(
      CurrentAccount.Get.Route,
    )

    if (dispatch) {
      if (user?.['@type'] === 'ConnectedUser') {
        dispatchGetPrices(dispatch)().catch((err) => console.warn('Cannot get Prices in getMyInfo', err))
      }

      dispatch({ type: UserTypes.UpdateUser, payload: { ...user, ...paramsToDispatch } })
    }

    return user
  }

/* *******************************
  ADDRESS SEARCH
******************************* */

export const searchAddressAsync = async (params: {
  addressText?: string
  geolocation?: { latitude: number; longitude: number }
}) => {
  const { addressText, geolocation } = params

  let searchParams: MapBoxAutocomplete.SearchParams
  if (addressText?.length) {
    searchParams = { addressText }
  } else if (geolocation) {
    searchParams = geolocation
  } else {
    throw new Error('MapBox Autocomplete - Missing param')
  }

  const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    MapBoxAutocomplete.Response,
    never,
    never,
    MapBoxAutocomplete.SearchParams
  >(MapBoxAutocomplete.Route, { searchParams })

  return response || []
}

export function searchAddressAsync2(searchParams: SearchWithoutGeo): Promise<AddressProvider[]>

export function searchAddressAsync2(searchParams: SearchWithGeo): Promise<Address[]>

export async function searchAddressAsync2(searchParams: SearchAddressParams): Promise<Address[] | AddressProvider[]> {
  const { input, geolocation, strict } = searchParams

  if (!geolocation && (!input || input.length < 3)) {
    return []
  }

  const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    GooglePlaceAutocomplete.Response,
    never,
    never,
    GooglePlaceAutocomplete.SearchParams
  >(GooglePlaceAutocomplete.Route, {
    searchParams: {
      ...(input ? { input } : {}),
      ...(geolocation
        ? {
            latitude: geolocation.latitude,
            longitude: geolocation.longitude,
          }
        : {}),
      ...(strict ? { strict } : {}),
    },
  })

  return response['hydra:member'] || []
}

export const retrieveAddressAsync = async (providerId: string) => {
  const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    GooglePlaceRetrieve.Response,
    never,
    never,
    { providerId: string }
  >(GooglePlaceRetrieve.Route, {
    searchParams: { providerId },
  })

  return response['hydra:member']?.[0] || null
}

/* *******************************
  API NOTIFICATIONS
******************************* */
export const dispatchNotificationsAsync =
  (dispatch?: AppDispatch) =>
  /**
   * Will refresh the api notifications
   * @returns Notifications
   */
  async (customerId: string): Promise<ApiNotification[]> => {
    const notifications = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
      GetApiNotifications.Response,
      never,
      GetApiNotifications.UrlParams
    >(GetApiNotifications.Route, { urlPathParams: { customerId } }).then((res) => res['hydra:member'])

    if (dispatch) {
      dispatch({ type: ApiNotificationsTypes.AddMultiple, payload: notifications })
    }

    return notifications
  }

/* *******************************
  UPDATE PASSWORD
******************************* */

export const updatePasswordAsync = async (oldPassword: string, newPassword: string, repeatNewPassword: string) => {
  await BasicAuthServiceHelper.requestAuthenticatedV2Async(UpdatePassword.Route, {
    body: {
      oldPassword,
      plainPassword: {
        first: newPassword,
        second: repeatNewPassword,
      },
    },
  })
}

/* *******************************
  CONTINUE REGISTER
******************************* */

export const dispatchContinueRegisterAsync =
  (dispatch?: AppDispatch) =>
  async ({ type, body }: ContinueRegister.ContinueRegister.Body): Promise<User> => {
    let response: ContinueRegister.ContinueRegister.Response
    switch (type) {
      case 'petowner':
        response = await requestAuthenticatedV2Async<typeof response, typeof body>(
          ContinueRegister.ContinueRegister.PetOwnerRoute,
          {
            body,
          },
        )
        break
      case 'borrower':
        response = await requestAuthenticatedV2Async<typeof response, typeof body>(
          ContinueRegister.ContinueRegister.BorrowerRoute,
          {
            body,
          },
        )
        break
    }

    if (!response) {
      throw new Error(`Unknown type: ${type}`)
    }

    if (dispatch) {
      dispatch({ type: UserTypes.UpdateUser, payload: response })
    }

    return response
  }

/* *******************************
  MODIFY REGISTER
******************************* */

export const dispatchModifyRegisterAsync =
  (dispatch: AppDispatch) =>
  async (body: ContinueRegister.ModifyRegister.Body): Promise<User> => {
    const response = await requestAuthenticatedV2Async<
      ContinueRegister.ModifyRegister.Response,
      ContinueRegister.ModifyRegister.Body
    >(ContinueRegister.ModifyRegister.Route, {
      body,
    })

    dispatch({ type: UserTypes.UpdateUser, payload: response })

    return response
  }

/* *******************************
  COMPLETE REGISTER
******************************* */

export const dispatchCompleteRegisterAsync =
  (dispatch: AppDispatch) =>
  async (body: ContinueRegister.CompleteRegister.Body): Promise<User> => {
    const response = await requestAuthenticatedV2Async<
      ContinueRegister.CompleteRegister.Response,
      ContinueRegister.CompleteRegister.Body
    >(ContinueRegister.CompleteRegister.Route, {
      body,
    })

    dispatch({ type: UserTypes.UpdateUser, payload: response })

    return response
  }

/* *******************************
  CONTINUE ANIMAL REGISTER
******************************* */

export const dispatchCreatePendingAnimalAsync =
  (dispatch: AppDispatch) =>
  async (body: ContinueRegister.CreateAnimal.Body): Promise<Animal> => {
    const response = await requestAuthenticatedV2Async<
      ContinueRegister.CreateAnimal.Response,
      ContinueRegister.CreateAnimal.Body
    >(ContinueRegister.CreateAnimal.Route, {
      body,
    })

    dispatch({ type: UserTypes.CreateAnimal, payload: response })

    return response
  }

export const dispatchUpdatePendingAnimalAsync =
  (dispatch: AppDispatch) =>
  async (animalId: string, body: ContinueRegister.UpdateAnimal.Body): Promise<Animal> => {
    const response = await requestAuthenticatedV2Async<
      ContinueRegister.UpdateAnimal.Response,
      ContinueRegister.UpdateAnimal.Body,
      ContinueRegister.UpdateAnimal.UrlParams
    >(ContinueRegister.UpdateAnimal.Route, {
      body,
      urlPathParams: {
        animalId,
      },
    })

    dispatch({ type: UserTypes.UpdateAnimal, payload: response })

    return response
  }

export const dispatchDeletePendingAnimalAsync =
  (dispatch: AppDispatch) =>
  async (animalId: string): Promise<Animal> => {
    const response = await requestAuthenticatedV2Async<
      ContinueRegister.DeleteAnimal.Response,
      ContinueRegister.DeleteAnimal.Body,
      ContinueRegister.DeleteAnimal.UrlParams
    >(ContinueRegister.DeleteAnimal.Route, {
      urlPathParams: {
        animalId,
      },
    })

    dispatch({ type: UserTypes.DeleteAnimal, payload: animalId })

    return response
  }

/* *******************************
  ANIMALS
******************************* */

export const dispatchCreateAnimalAsync =
  (dispatch: AppDispatch) =>
  async (body: Animals.CreateAnimal.Body): Promise<Animal> => {
    const response = await requestAuthenticatedV2Async<Animals.CreateAnimal.Response, Animals.CreateAnimal.Body>(
      Animals.CreateAnimal.Route,
      {
        body,
      },
    )

    dispatch({ type: UserTypes.CreateAnimal, payload: response })

    return response
  }

export const dispatchUpdateAnimalAsync =
  (dispatch: AppDispatch) =>
  async (animalId: string, body: Animals.UpdateAnimal.Body): Promise<Animal> => {
    const response = await requestAuthenticatedV2Async<
      Animals.UpdateAnimal.Response,
      Animals.UpdateAnimal.Body,
      Animals.UpdateAnimal.UrlParams
    >(Animals.UpdateAnimal.Route, {
      body,
      urlPathParams: {
        animalId,
      },
    })

    dispatch({ type: UserTypes.UpdateAnimal, payload: response })

    return response
  }

export const dispatchDeleteAnimalAsync =
  (dispatch: AppDispatch) =>
  async (animalId: string): Promise<Animal> => {
    const response = await requestAuthenticatedV2Async<
      Animals.DeleteAnimal.Response,
      Animals.DeleteAnimal.Body,
      Animals.DeleteAnimal.UrlParams
    >(Animals.DeleteAnimal.Route, {
      urlPathParams: {
        animalId,
      },
    })

    dispatch({ type: UserTypes.DeleteAnimal, payload: animalId })

    return response
  }

/* *******************************
  UPDATE USER
******************************* */

export const dispatchUpdateUser =
  (dispatch: AppDispatch) =>
  async (body: RecursivePartial<ConnectedUser>): Promise<ConnectedUser | null> => {
    if (Object.values(body).some((val) => val !== undefined)) {
      const { token, refreshToken, mercureToken, mercurePublishUrl, ...response } =
        await BasicAuthServiceHelper.requestAuthenticatedV2Async<
          CurrentAccount.Update.Response,
          CurrentAccount.Update.Body
        >(CurrentAccount.Update.Route, { body })

      if (token && refreshToken) {
        _saveAuthInfo({ token, refreshToken, mercureToken, mercurePublishUrl })
      }

      dispatch({ type: UserTypes.UpdateUser, payload: response })

      return response
    }

    return null
  }

/** This is just a shortcut */
export const dispatchUpdateUserSettings =
  (dispatch: AppDispatch) =>
  async (settings: RecursivePartial<ConnectedUser['settings']>): Promise<ConnectedUser | null> => {
    return dispatchUpdateUser(dispatch)({ settings })
  }

/* *******************************
  PICTURES
******************************* */

export const addMemberPictureAsync = async (body: Pictures.AddMember.Body): Promise<Picture> => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<Pictures.AddMember.Response, Pictures.AddMember.Body>(
    Pictures.AddMember.Route,
    { body },
  )
}
export const addAnimalPictureAsync = async (animalId: string, body: Pictures.AddAnimal.Body): Promise<Picture> => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    Pictures.AddAnimal.Response,
    Pictures.AddAnimal.Body,
    Pictures.AddAnimal.UrlParams
  >(Pictures.AddAnimal.Route, { body, urlPathParams: { animalId } })
}

export const deletePictureAsync = async (pictureId: string): Promise<void> => {
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<
    Pictures.Delete.Response,
    Pictures.Delete.Body,
    Pictures.Delete.UrlParams
  >(Pictures.Delete.Route, {
    urlPathParams: { pictureId },
  })
}

/* *******************************
  CURRENT INSURANCE
******************************* */

export const dispatchSetCurrentInsurance =
  (dispatch: AppDispatch) =>
  /**
   * Will set the new insurance, and then refresh the current user
   * @param insuranceId The Id of the new insurance
   * @param insuranceDataId The data (number) associated to the insurance
   * @returns
   */
  async (insuranceId: string, insuranceDataId?: string): Promise<Insurance> => {
    try {
      const body = insuranceDataId
        ? {
            insuranceData: {
              id: insuranceDataId,
            },
          }
        : {}
      const insurance = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
        SetInsurance.Response,
        SetInsurance.Body,
        SetInsurance.UrlParams
      >(SetInsurance.Route, { urlPathParams: { insuranceId }, body })

      await dispatchGetMyInfoAsync(dispatch)()

      return insurance
    } catch (err) {
      // When error, just refresh the attempts number, and rethrow error
      try {
        await InsurancesServiceHelper.dispatchGetInsuranceAttemptsAsync(dispatch)(insuranceId)
      } catch (error) {
        // Silence this error
      }

      throw err
    }
  }

export const dispatchCheckInsurance =
  () =>
  async (insuranceId: string, identifier: string): Promise<void> => {
    return InsurancesServiceHelper.checkInsuranceAsync()(insuranceId, identifier)
  }

export const dispatchSubscribeInsurance =
  () =>
  async (insuranceId: string, identifier: string): Promise<void> => {
    return InsurancesServiceHelper.subscribeInsuranceAsync()(insuranceId, identifier)
  }

/* *******************************
  PRICES
******************************* */

export const getPrice = async (planIri: IRI | undefined): Promise<PricePlan | null> => {
  if (!planIri) {
    throw new Error('planIri is undefined')
  }
  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<Prices.GetPricePlan.Response>(
    Prices.GetPricePlan.Route(planIri),
  )
}

// import pricePlans from './stubs/pricePlansWithTrial'

export const dispatchGetPrices = (dispatch: AppDispatch) => async (): Promise<PricePlan[]> => {
  const { items } = await BasicAuthServiceHelper.getAllHydraCollectionPagesV2Async<Prices.Get.Response>(
    Prices.Get.Route,
  )

  // const items = pricePlans

  dispatch({ type: UserTypes.UpdatePrices, payload: items || [] })

  return items
}

// import discountPlan from './stubs/priceDiscountPlan'

export const sendCoupon = async (plan: PricePlan, discountCode: string): Promise<PricePlan> => {
  // return discountPlan

  const body = {
    ...plan,
    discountCode,
  }

  return await BasicAuthServiceHelper.requestAuthenticatedV2Async<Prices.SendCoupon.Response, Prices.SendCoupon.Body>(
    Prices.SendCoupon.Route,
    { body },
  )
}

/* *******************************
  AUTO RENEW
******************************* */
export const renewAsync = async (renewIRI: IRI | undefined) => {
  if (!renewIRI) {
    throw new Error('renewIRI undefined')
  }

  await BasicAuthServiceHelper.requestAuthenticatedV2Async<RenewSubscription.Response, RenewSubscription.Body>(
    RenewSubscription.Route(renewIRI),
  )
}
export const disableRenewAsync = async (renewIRI: IRI | undefined) => {
  if (!renewIRI) {
    throw new Error('renewIRI undefined')
  }

  await BasicAuthServiceHelper.requestAuthenticatedV2Async<RenewSubscription.Response, RenewSubscription.Body>(
    RenewSubscription.Route(renewIRI),
  )
}

/* *******************************
  DELETE ACCOUNT
******************************* */
export const deleteAccountAsync = async (deleteIRI: IRI | undefined) => {
  if (!deleteIRI) {
    throw new Error('deleteIRI undefined')
  }

  await BasicAuthServiceHelper.requestAuthenticatedV2Async(DeleteAccount.Route(deleteIRI))
}

/* *******************************
  DISABLED ACCOUNT
******************************* */
export const disableAccountAsync = async (disableIRI: string | undefined) => {
  if (!disableIRI) {
    throw new Error('disableIRI undefined')
  }

  await BasicAuthServiceHelper.requestAuthenticatedV2Async(DisableAccount.Route(disableIRI))
}

/* *******************************
  ID CHECK
******************************* */

export const dispatchSendIDImageAsync = (dispatch: AppDispatch) => async (recto: File, verso?: File) => {
  const body: IDCheck.Certify.Body = {
    recto: await convertToAPIReadyBase64Async(recto, true),
  }

  if (verso) {
    body.verso = await convertToAPIReadyBase64Async(verso, true)
  }

  await BasicAuthServiceHelper.requestAuthenticatedV2Async<IDCheck.Certify.Response, IDCheck.Certify.Body>(
    IDCheck.Certify.Route,
    { body },
  )

  return await dispatchGetMyInfoAsync(dispatch)()
}

export const dispatchSendIDMRZAsync = (dispatch: AppDispatch) => async (line1: string, line2: string) => {
  await BasicAuthServiceHelper.requestAuthenticatedV2Async<IDCheck.MRZ.Response, IDCheck.MRZ.Body>(IDCheck.MRZ.Route, {
    body: { line1, line2 },
  })

  // Re Update the current user after certified updated
  return await dispatchGetMyInfoAsync(dispatch)()
}
